/**
 * Church-timezone date helpers.
 *
 * Members are in Singapore and reconcile giving and eGIRO dates against bank
 * statements, so every date the app renders is Singapore time regardless of
 * what the device's clock is set to. Never format a member-facing date with a
 * bare `dayjs(value)` — that inherits the host timezone and can land a
 * transaction on the wrong day.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const CHURCH_TIMEZONE = 'Asia/Singapore';

// Deliberately no `dayjs.tz.setDefault()`: that mutates the dayjs singleton every
// other module shares. Pass the zone explicitly instead, as `churchTime` does.

// Trailing `Z` or `+08:00` / `-0500` — i.e. the value names an absolute instant.
// Hour-only offsets (`+08`) are deliberately not matched: they are indistinguishable
// from the last field of a `YYYY-MM-DD` date, and misreading those is the worse bug.
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Read a value as Singapore time.
 *
 * A string with no offset is a Singapore wall clock and is taken at face value;
 * anything else names an instant and is converted to Singapore time. Both
 * branches produce the same output on every host.
 *
 * The one exception is the giving API's `giving_date`, whose offset-less
 * timestamps mean UTC. `myGiving.js` reads those with
 * `dayjs.utc(...).tz(CHURCH_TIMEZONE)` rather than this helper. The two rules
 * disagree on the same string shape because each preserves what its own call
 * site rendered before the migration: `giving_date` went through `moment.utc()`,
 * everything else through a plain `moment()` that read the device's clock — and
 * on a member's Singapore device, that clock was Singapore time.
 *
 * @param {string|number|Date} [value] defaults to now
 * @returns {dayjs.Dayjs}
 */
export const churchTime = value =>
  typeof value === 'string' && !HAS_OFFSET.test(value)
    ? dayjs.tz(value, CHURCH_TIMEZONE)
    : dayjs(value).tz(CHURCH_TIMEZONE);

/**
 * Format a church update's posted time for display.
 *
 * @param {string} time
 * @param {{ verbose?: boolean }} [options] verbose keeps the clock time on
 *   older updates and labels today's, for the update detail page.
 * @returns {string} e.g. `01:00PM` / `28 AUG`, or `TODAY, 01:00PM` / `28 AUG, 11:59PM`
 */
export function formatPostedTime(time, { verbose = false } = {}) {
  const posted = churchTime(time);
  const clock = posted.format('hh:mmA');

  if (posted.isSame(churchTime(), 'day')) {
    return verbose ? `TODAY, ${clock}` : clock;
  }

  const day = posted.format('DD MMM').toUpperCase();
  return verbose ? `${day}, ${clock}` : day;
}

// The dayjs singleton with `utc` and `timezone` already extended. Import dayjs
// from here — not from 'dayjs' — anywhere you need `.utc()` or `.tz()`, so the
// plugins are guaranteed loaded rather than depending on some other module
// having been imported first. That load-order coupling is what made the old
// `moment.utc(...).tz(...)` in myGiving.js work only by accident.
export default dayjs;
